import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import AdminRoute from '@/components/AdminRoute';
import LandingPage from '@/components/LandingPage';
import UserDashboard from '@/components/UserDashboard';
import Dashboard from '@/components/Dashboard';
import AuthPage from '@/components/AuthPage';
import AuthCallback from '@/components/AuthCallback';
import AccountPage from '@/components/AccountPage';
import AdminPage from '@/components/AdminPage';
import BuyCreditsPage from '@/components/BuyCreditsPage';
import PaymentSuccessPage from '@/components/PaymentSuccessPage';
import PaymentCancelPage from '@/components/PaymentCancelPage';
import ReferralPage from '@/components/ReferralPage';
import HelpPage from '@/components/HelpPage';
import DownloadPage from '@/components/DownloadPage';
import StrategiesPage from '@/components/StrategiesPage';
import InterfaceGuidePage from '@/components/InterfaceGuidePage';
import ForgotPasswordPage from '@/components/ForgotPasswordPage';
import { Toaster } from '@/components/ui/sonner';

/**
 * AppRouter - Main routing with session_id detection
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * 
 * Route Structure:
 * - / (root): Landing page (public marketing)
 * - /auth: Login/Register
 * - /dashboard: User Dashboard (for all authenticated users)
 * - /admin: Admin Panel with Trading (admin only)
 * - /trading: Trading Dashboard (admin only - same as old Dashboard)
 * 
 * Detects session_id in URL fragment SYNCHRONOUSLY (during render, NOT in useEffect)
 * This prevents race conditions with ProtectedRoute checks.
 */
function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL fragment (Emergent Google OAuth callback)
  // Must be synchronous to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route path="/strategies" element={<StrategiesPage />} />
      <Route path="/interface-guide" element={<InterfaceGuidePage />} />
      
      {/* Protected routes - All authenticated users */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <UserDashboard />
        </ProtectedRoute>
      } />
      <Route path="/account" element={
        <ProtectedRoute>
          <AccountPage />
        </ProtectedRoute>
      } />
      <Route path="/buy-credits" element={
        <ProtectedRoute>
          <BuyCreditsPage />
        </ProtectedRoute>
      } />
      <Route path="/referrals" element={
        <ProtectedRoute>
          <ReferralPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <PaymentSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/cancel" element={
        <ProtectedRoute>
          <PaymentCancelPage />
        </ProtectedRoute>
      } />
      <Route path="/help" element={
        <ProtectedRoute>
          <HelpPage />
        </ProtectedRoute>
      } />
      
      {/* Admin-only routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminPage />
        </AdminRoute>
      } />
      <Route path="/trading" element={
        <AdminRoute>
          <Dashboard />
        </AdminRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
